import React from 'react';
import { getOnboardingUrl } from "../../utils/portalUrl";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaQuestionCircle, FaBolt, FaSyringe, FaGraduationCap, FaUmbrellaBeach, FaWalking, FaAppleAlt } from 'react-icons/fa';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import SEO from '../../Components/SEO/SEO';
import { BUSINESSES_OVERVIEW_META } from '../../seo/pageMeta';
import styles from './BusinessesOverview.module.css';

// Photos
import BarberPhoto   from '../../assets/Industry_Barber_Software_Fresha.05bb918e.jpg';
import SalonPhoto    from '../../assets/Industry_Salon_Software_Fresha.e60d3dd6.jpg';
import NailsPhoto    from '../../assets/Industry_Nails_Software_Fresha.46347c46.jpg';
import DentalPhoto   from '../../assets/Industry_dental.jpg';
import ClinicsPhoto  from '../../assets/clinic-aest.jpeg';
import MassagePhoto  from '../../assets/Industry_Massage_Software_Fresha.be6af04f.jpg';
import SpaPhoto      from '../../assets/Industry_Spa_Software_Fresha.b66df7eb.jpg';
import MedSpaPhoto   from '../../assets/Industry_MedSpa_Software_Fresha.0afc4a22.jpg';
import PetPhoto      from '../../assets/Industry_PET_Software_Fresha.6ff140cf.jpg';
import FitnessPhoto  from '../../assets/Industry_Fitness_Software_Fresha.beeb275f.jpg';
import TanningPhoto  from '../../assets/Industry_Tanning_One-Solution_Fresha.cab9793e.jpg';
import TattooPhoto   from '../../assets/Industry_Tattoo-Piercing_Software_Fresha.d4736b77.jpg';
import OnlinePhoto   from '../../assets/Industry_Salon_Fresha_software_you_can_trust.239a423b.jpg';
import BeautyPhoto   from '../../assets/beauty_center.jpeg';

// Order: Barber, Hair, Nails, Dental, Aesthetic Clinic, Massage, Beauty Center,
//        Spa, Med Spa, Pet Grooming & Vets, Fitness, Tanning, Tattoo, Online, Other
const BIZ_ITEMS = [
  { key: 'barber',  path: '/for/barber-shops', photo: BarberPhoto,  color: '#7c3aed' },
  { key: 'hair',    path: '/for/hair-salons',  photo: SalonPhoto,   color: '#d946a8' },
  { key: 'nails',   path: '/for/nail-salons',  photo: NailsPhoto,   color: '#ec4899' },
  { key: 'dental',  path: '/for/dental',       photo: DentalPhoto,  color: '#0ea5e9' },
  { key: 'clinics', path: '/for/clinics',      photo: ClinicsPhoto, color: '#8b5cf6' },
  { key: 'massage', path: '/for/massage',      photo: MassagePhoto, color: '#0891b2' },
  { key: 'beauty',  path: '/for/beauty',       photo: BeautyPhoto,  color: '#f43f5e' },
  { key: 'spa',     path: '/for/spa',          photo: SpaPhoto,     color: '#10b981' },
  { key: 'medspa',  path: '/for/med-spa',      photo: MedSpaPhoto,  color: '#059669' },
  { key: 'pet',     path: '/for/pet-grooming', photo: PetPhoto,     color: '#16a34a' },
  { key: 'fitness', path: '/for/fitness',      photo: FitnessPhoto, color: '#ef4444' },
  { key: 'tanning', path: '/for/tanning',      photo: TanningPhoto, color: '#d97706' },
  { key: 'tattoo',  path: '/for/tattoo',       photo: TattooPhoto,  color: '#374151' },
  { key: 'online',  path: '/for/online',       photo: OnlinePhoto,  color: '#2d8cff' },
  // ── Published by the 2026-09 expansion. No photo: the existing photo set is
  //    competitor-sourced and is not being extended, so these lean on their
  //    icon and colour here and on their detail-row imagery on the page.
  { key: 'laser',     path: '/for/laser-hair-removal', photo: null, Icon: FaBolt,          color: '#0891b2' },
  { key: 'aesthetic', path: '/for/aesthetic-clinics',  photo: null, Icon: FaSyringe,       color: '#8b5cf6' },
  { key: 'academies', path: '/for/academies',          photo: null, Icon: FaGraduationCap, color: '#7c3aed' },
  { key: 'rentals',   path: '/for/vacation-rentals',   photo: null, Icon: FaUmbrellaBeach, color: '#0ea5e9' },
  { key: 'physio',    path: '/for/physiotherapy',      photo: null, Icon: FaWalking,       color: '#059669' },
  { key: 'nutrition', path: '/for/nutrition',          photo: null, Icon: FaAppleAlt,      color: '#16a34a' },
  { key: 'other',   path: '/for/other',        photo: null, Icon: FaQuestionCircle, color: '#f59e0b' },
];

const BusinessesOverview = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <SEO {...BUSINESSES_OVERVIEW_META} />
      <Navbar />

      <section className={styles.hero}>
        <div className="container">
          <h1 data-aos="fade-up">{t('businesses.overview.heroTitle')}</h1>
          <p data-aos="fade-up" data-aos-delay="100">{t('businesses.overview.heroSubtitle')}</p>
          <a href={getOnboardingUrl("cta")}
            className={styles.heroCta} data-aos="fade-up" data-aos-delay="200">
            {t('businesses.overview.cta')}
          </a>
        </div>
      </section>

      <section className={styles.grid}>
        <div className="container">
          <div className={styles.photosGrid}>
            {BIZ_ITEMS.map(({ key, path, photo, color, Icon }, i) => {
              const label = t(`businesses.cards.${key}.title`);
              if (photo) {
                return (
                  <Link to={path} key={key} className={styles.photoCard}
                    data-aos="fade-up" data-aos-delay={Math.min(i * 40, 200)}>
                    <img src={photo} alt={label} className={styles.photoImg} />
                    <div className={styles.photoOverlay} />
                    <span className={styles.photoLabel}>{label}</span>
                  </Link>
                );
              }
              return (
                <Link to={path} key={key} className={styles.fallbackCard}
                  style={{ background: `linear-gradient(135deg, ${color}dd, ${color}99)` }}
                  data-aos="fade-up" data-aos-delay={Math.min(i * 40, 200)}>
                  {Icon && <Icon className={styles.fallbackIcon} />}
                  <span className={styles.fallbackLabel}>{label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className="container">
          <h2>{t('businesses.common.ctaTitle')}</h2>
          <p>{t('businesses.common.ctaSubtitle')}</p>
          <a href={getOnboardingUrl("cta")} className={styles.ctaButton}>
            {t('businesses.common.cta')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessesOverview;
